import * as React from 'react'
import './user-list.scss'
import { Link } from 'react-router-dom'
import { AppState } from '@halo/stores/app.state'
import { AppService } from '@halo/services/app.service'
import { UserService } from '@halo/services/user.service'
import { Container } from 'typescript-ioc/es5'
import { UserDto } from '@halo/dtos/user.dto'
import { IApiResponse } from '@pushly/aqe/lib/interfaces'
import { Button, Well } from '@pushly/aqe/lib/components'
import { classnames } from '@halo/_utils/classnames'
import { Loading3QuartersOutlined } from '@ant-design/icons'
import { Form } from '@ant-design/compatible'
import '@ant-design/compatible/assets/index.css'
import { Input, Modal, Select, Skeleton, Table, Tooltip } from 'antd'
import { Select as AqeSelect } from '@halo/components/select/select'
import { AntdTableEmptyPlaceholder } from '@halo/components/antd-table-empty-placeholder/antd-table-empty-placeholder'
import { TableRowEntityDisplay } from '@halo/components/table-row-entity-display/table-row-entity-display'
import { StatusBadge } from '@halo/components/badges/status-badge'
import { FormComponentProps } from '@ant-design/compatible/lib/form'
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT } from '@halo/constants'
import { legacyValidateFields } from '@halo/_utils/antd'
import { simpleFormErrorNotification } from '@halo/_utils/notifications'
import { AccountDto } from '@halo/dtos/account.dto'
import { AccountService } from '@halo/services/account.service'
import { DomainService } from '@halo/services/domain.service'
import { UserType, UserTypeId } from '@halo/enums/user-type'
import * as deepEqual from 'react-fast-compare'
import { UserAccessWell } from '@halo/components/user-data-form/user-access-well'
import { StatusType } from '@pushly/aqe/lib/enums'
import AccountUserModel from '@halo/models/access-control/account-user.model'
import DomainUserModel from '@halo/models/access-control/domain-user.model'
import { AccessRoleId, ExternalApiAccessRoleIds } from '@halo/enums/access-role.enum'
import DataTableView from '../data-table-view/data-table-view'
import { AbilityAction } from '@halo/enums/ability-action.enum'
import { asCaslSubject } from '@halo/stores/ability.state'
import { SubjectEntity } from '@halo/enums/ability-entity.enum'
import SwSimpleLinkPagination from '@halo/components/sw-simple-link-pagination/sw-simple-link-pagination'
import { IApiResponsePaginationLinks } from '@halo/interfaces/api-response-pagination-links'

interface IAddUserPrompt extends FormComponentProps {
    accountId?: number
    domainId?: number
    onOk: (domainDto: Partial<UserDto> & { permissions?: AccountUserModel[] }) => any
    onCancel: () => any
    visible: boolean
}

interface IAddUserPromptState {
    user: UserDto
}

export const AddUserPrompt = Form.create<IAddUserPrompt>()(
    class extends React.Component<IAddUserPrompt, IAddUserPromptState> {
        public state: IAddUserPromptState = {
            user: new UserDto(),
        }

        protected appState: AppState
        protected appService: AppService
        protected userService: UserService
        protected accountService: AccountService
        protected domainService: DomainService

        protected displayNameRef: any

        public constructor(props: IAddUserPrompt) {
            super(props)

            this.appState = Container.get(AppState)
            this.appService = Container.get(AppService)
            this.userService = Container.get(UserService)
            this.accountService = Container.get(AccountService)
            this.domainService = Container.get(DomainService)
        }

        public componentDidMount(): void {
            this.autofocusDisplayName()
        }

        public componentWillUnmount(): void {
            // Destroy any open modals to prevent memory leaks
            Modal.destroyAll()
        }

        public render() {
            const actingUserIsInternal = this.appState.currentUser?.isInternalUser ?? false
            const selectedUserType = this.props.form.getFieldValue('userType') ?? UserType.EXTERNAL
            const selectedUserTypeIsInternal = selectedUserType === UserType.INTERNAL

            const additionalEmailRules: any[] = []
            if (!selectedUserTypeIsInternal) {
                additionalEmailRules.push({
                    type: 'email',
                    message: 'A valid email address is required.',
                })
            }

            return (
                <Modal
                    className={classnames('add-user-prompt')}
                    title="Add User"
                    visible={this.props.visible}
                    onOk={this.handleOk}
                    okText="Submit"
                    onCancel={this.handleCancel}
                >
                    <Skeleton loading={false} active={true} title={false}>
                        <Well className="nested" mode="ghost" hideHeader={true} hideFooter={true}>
                            <Form {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT} className="hide-inline-errors">
                                <Well className="nested" mode="ghost" hideHeader={true} hideFooter={true}>
                                    <Form.Item label="User Type">
                                        {this.props.form.getFieldDecorator('userType', {
                                            initialValue: selectedUserType,
                                            rules: [
                                                {
                                                    required: true,
                                                    message: 'User type is required.',
                                                },
                                            ],
                                        })(
                                            <Select<UserType>
                                                size="small"
                                                onChange={(type) => {
                                                    this.setState(({ user }) => {
                                                        return {
                                                            user: UserDto.fromApiResponse({
                                                                ...user,
                                                                user_type: type,
                                                                user_type_id:
                                                                    type === UserType.EXTERNAL
                                                                        ? UserTypeId.EXTERNAL
                                                                        : UserTypeId.INTERNAL,
                                                            }),
                                                        }
                                                    })
                                                }}
                                            >
                                                <Select.Option value={UserType.EXTERNAL}>
                                                    External Platform User
                                                </Select.Option>
                                                {actingUserIsInternal && (
                                                    <Select.Option value={UserType.INTERNAL}>
                                                        Internal Platform User
                                                    </Select.Option>
                                                )}
                                            </Select>,
                                        )}
                                    </Form.Item>

                                    <Form.Item label="Email Address">
                                        {this.props.form.getFieldDecorator('email', {
                                            rules: [
                                                {
                                                    required: true,
                                                    message: 'Email address is required.',
                                                },
                                                ...additionalEmailRules,
                                            ],
                                        })(
                                            <Input
                                                size="small"
                                                autoComplete="off"
                                                addonAfter={
                                                    selectedUserTypeIsInternal && (
                                                        <span>
                                                            <b>@pushly.com</b>
                                                        </span>
                                                    )
                                                }
                                            />,
                                        )}
                                    </Form.Item>
                                </Well>

                                {this.props.form.getFieldDecorator('permissions', {
                                    rules: [
                                        {
                                            required: true,
                                            message: 'Initial user permissions are required.',
                                        },
                                    ],
                                })(
                                    <UserAccessWell
                                        mode="nested"
                                        accountId={this.props.accountId}
                                        user={this.state.user}
                                    />,
                                )}
                            </Form>
                        </Well>
                    </Skeleton>
                </Modal>
            )
        }

        protected handleOk = async () => {
            try {
                await legacyValidateFields(this.props.form)
                const formValues = this.props.form.getFieldsValue()

                const dto: Partial<UserDto> & Partial<{ permissions: AccountUserModel[] }> = {}
                dto.user_type = formValues.userType
                dto.email = formValues.email

                // ensure @pushly.com added to internal users
                if (dto.user_type === UserType.INTERNAL) {
                    dto.email = dto.email.replace(/@.+/gi, '') + '@pushly.com'
                }

                const permissions: AccountUserModel[] = []
                if (formValues.permissions) {
                    const account = Object.values<AccountUserModel>(formValues.permissions.accounts ?? {}).find(
                        (a: AccountUserModel) => a.getAccountId() === this.props.accountId,
                    )

                    if (account) {
                        const domains = Object.values<DomainUserModel>(formValues.permissions.domains ?? {})
                        if (domains.length) {
                            account.setDomainRecords(domains)
                        }

                        permissions.push(account)
                    }
                }

                dto.permissions = permissions

                this.props.onOk(dto)
            } catch (err) {
                simpleFormErrorNotification(err)
            }
        }

        protected handleCancel = () => {
            this.props.onCancel()
        }

        protected autofocusDisplayName(): void {
            if (this.props.visible) {
                if (!this.displayNameRef) {
                    setTimeout(() => this.autofocusDisplayName(), 50)
                    return
                }

                if (this.props.accountId) {
                    this.displayNameRef.focus()
                }
            }
        }
    },
)

interface IUserList {
    options: {
        account?: AccountDto
    }
}

interface IUserListState {
    loading: boolean
    users: UserDto[]
    usersMeta: any
    page: number
    pageSize: number
    query?: string
    typeFilter: string[]
    statusFilter: string[]
    paginationLinks?: IApiResponsePaginationLinks

    showAddUserPrompt: boolean
}

export class UserList extends React.Component<IUserList, IUserListState> {
    protected appState: AppState
    protected appService: AppService
    protected userService: UserService

    public constructor(props: any) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.userService = Container.get(UserService)

        const typeFilter = [UserType.EXTERNAL, UserType.EXTERNAL_API]
        // default external user view to include internal users in default filter
        // - internal_api users should never be visible to external view
        if (!this.appState.currentUser?.isInternalUser) {
            typeFilter.push(UserType.INTERNAL)
        }

        this.state = {
            loading: true,
            users: [],
            usersMeta: {},
            page: 1,
            pageSize: 15,
            showAddUserPrompt: false,
            typeFilter,
            statusFilter: [
                StatusType[StatusType.ACTIVE],
                StatusType[StatusType.INVITED],
                StatusType[StatusType.PASSWORD_RESET],
            ],
        }
    }

    public componentDidMount(): void {
        this.fetchUsers()
    }

    public componentWillUnmount(): void {
        // Destroy any open modals to prevent memory leaks
        Modal.destroyAll()
    }

    public render() {
        const userTypeOptions = [{ value: UserType.EXTERNAL, label: 'External' }]

        if (this.appState.currentUser?.isInternalUser) {
            userTypeOptions.push(
                { value: UserType.INTERNAL, label: 'Internal' },
                { value: UserType.INTERNAL_API, label: 'Internal (API)' },
            )
        }

        return (
            <div className={classnames('user-list')}>
                <DataTableView
                    actions={
                        this.props.options.account && [
                            <Button key="add" size="small" type="primary" onClick={this.showAddUserPrompt}>
                                New User
                            </Button>,
                        ]
                    }
                    onSearchChange={this.searchUsers}
                    searchPlaceholder="Search ..."
                    filtersOverride={
                        <React.Fragment>
                            <div className="data-table-filter">
                                <AqeSelect
                                    className="type-filter data-table-filter-select"
                                    dropdownClassName="data-table-filter-select-overlay"
                                    mode="multiple"
                                    size="small"
                                    prefix="User Type"
                                    disableSearch={true}
                                    disableSelectAll={true}
                                    defaultValue={this.state.typeFilter}
                                    options={userTypeOptions}
                                    onClose={this.filterUserTypes}
                                    maxDisplayCount={2}
                                    maxDisplayFormatter={(options: any[]) => {
                                        return (
                                            <Tooltip title={options.map((o) => o.label).join(', ')}>
                                                {options.length} Selected
                                            </Tooltip>
                                        )
                                    }}
                                />
                            </div>
                            <div className="data-table-filter">
                                <AqeSelect
                                    className="status-filter data-table-filter-select"
                                    dropdownClassName="data-table-filter-select-overlay"
                                    mode="multiple"
                                    size="small"
                                    prefix="Status"
                                    disableSearch={true}
                                    disableSelectAll={true}
                                    defaultValue={this.state.statusFilter}
                                    options={[
                                        { value: StatusType[StatusType.ACTIVE], label: 'Active' },
                                        { value: StatusType[StatusType.DISABLED], label: 'Disabled' },
                                        { value: StatusType[StatusType.INVITED], label: 'Invited' },
                                        { value: StatusType[StatusType.PASSWORD_RESET], label: 'Password Reset' },
                                    ]}
                                    onClose={this.filterUserStatuses}
                                    maxDisplayCount={2}
                                    maxDisplayFormatter={(options: any[]) => {
                                        return (
                                            <Tooltip title={options.map((o) => o.label).join(', ')}>
                                                {options.length} Selected
                                            </Tooltip>
                                        )
                                    }}
                                />
                            </div>
                        </React.Fragment>
                    }
                >
                    <Table
                        size="small"
                        bordered={false}
                        dataSource={this.state.users}
                        pagination={false}
                        footer={(currentTableData) => (
                            <SwSimpleLinkPagination
                                currentTableData={currentTableData}
                                links={this.state.paginationLinks}
                                onPrev={(_ev, _link, config) => {
                                    this.setState({ page: config.current! }, () => this.fetchUsers())
                                }}
                                onNext={(_ev, _link, config) => {
                                    this.setState({ page: config.current! }, () => this.fetchUsers())
                                }}
                            />
                        )}
                        rowKey={(record) => record.id.toString()}
                        loading={
                            this.state.loading && {
                                indicator: <Loading3QuartersOutlined spin={true} />,
                            }
                        }
                        locale={{
                            emptyText: <AntdTableEmptyPlaceholder text="No Users" icon="user" />,
                        }}
                    >
                        {this.appState.currentUser?.isInternalUser && (
                            <Table.Column className="id" title="ID" dataIndex="id" />
                        )}
                        <Table.Column className="user" title="User" render={this.renderUserColumn} />
                        <Table.Column className="type" title="Type" render={this.renderUserTypeColumn} />
                        <Table.Column className="status" title="Status" render={this.renderStatusColumn} />
                        <Table.Column className="actions" title="Actions" render={this.renderRowActions} />
                    </Table>
                </DataTableView>

                <AddUserPrompt
                    key={`${this.state.showAddUserPrompt ? 'v' : 'h'}-add-user`}
                    visible={this.state.showAddUserPrompt}
                    accountId={this.props.options.account?.id ? this.props.options.account.id : undefined}
                    onOk={this.handleUserAdd}
                    onCancel={this.hideAddUserPrompt}
                />
            </div>
        )
    }

    protected renderUserColumn = (_: string, user: UserDto): React.ReactNode => {
        const name: string = user.name || user.email

        return <TableRowEntityDisplay title={<Link to={this.buildUserUrl(user)}>{name}</Link>} status={user.email} />
    }

    protected renderUserTypeColumn = (_: string, user: UserDto): React.ReactNode => {
        let text: any = '--'

        switch (user.user_type_id) {
            case UserTypeId.INTERNAL:
                text = 'Internal'
                break
            case UserTypeId.EXTERNAL:
                text = 'External'
                break
        }

        if (
            user.user_role_id === AccessRoleId.INTERNAL_API_USER ||
            ExternalApiAccessRoleIds.includes(user.user_role_id)
        ) {
            text = 'API User'
        }

        return text
    }

    protected renderStatusColumn = (_: string, user: UserDto): React.ReactNode => {
        return <StatusBadge type="user" status={user.status} expanded={true} />
    }

    protected renderRowActions = (_: any, user: UserDto): React.ReactNode => {
        const isSelf = this.appState.currentUser?.id === user.id
        const userDisabled = user.status_id === StatusType.DISABLED
        const showReInvite = this.props.options?.account?.id && !isSelf && user.status_id === StatusType.INVITED
        const showResetPassword =
            this.props.options?.account?.id && !isSelf && user!.status_id !== StatusType.INVITED && !user.isApiUser
        const showDisableUser = !isSelf && (this.appState.currentUser?.isInternalUser || !user.isInternalUser)
        const canUpdateUser =
            this.props.options.account &&
            this.appState.abilityStore.can(
                AbilityAction.UPDATE,
                asCaslSubject(SubjectEntity.USER, {
                    ...user,
                    userTypeId: user.user_type_id,
                    accountId: this.props.options.account.id,
                }),
            )

        const actions: any[] = []

        if (canUpdateUser) {
            if (userDisabled) {
                actions.push({
                    text: 'Enable',
                    icon: 'eye',
                    onClick: this.handleEnable,
                    confirm: {
                        okText: 'Enable',
                        title: 'Enable User',
                        iconType: 'question-circle',
                        content: <>Are you sure you want to enable this user?</>,
                    },
                    user,
                })
            } else {
                if (showDisableUser) {
                    actions.push({
                        text: 'Disable',
                        icon: 'eye',
                        onClick: this.handleDisable,
                        confirm: {
                            okText: 'Disable',
                            title: 'Disable User',
                            iconType: 'warning',
                            content: <>Are you sure you want to disable this user?</>,
                        },
                        user,
                    })
                }
                if (showReInvite) {
                    actions.push({
                        text: 'Re-Invite',
                        icon: 'mail',
                        onClick: this.handleReInvite,
                        user,
                    })
                }
                if (showResetPassword) {
                    actions.push({
                        text: 'Password Reset',
                        icon: 'lock',
                        onClick: this.handleResetPassword,
                        user,
                    })
                }
            }
        }

        return (
            <Button
                size="small"
                altHref={this.buildUserUrl(user)}
                onClick={() => this.jumpToUser(user)}
                actions={actions.length > 0 ? actions : undefined}
            >
                View
            </Button>
        )
    }

    protected buildUserUrl(user: UserDto): string {
        let url = `/users/${user.id}`

        const accountId = this.props.options.account?.id
        if (accountId) {
            url = `/organizations/${accountId}${url}`
        }

        return url
    }

    protected jumpToUser(user: UserDto): void {
        this.appService.route(this.buildUserUrl(user), {
            account: this.props.options.account,
            user,
        })
    }

    protected showAddUserPrompt = async (): Promise<void> => {
        return this.setState({ showAddUserPrompt: true })
    }

    protected hideAddUserPrompt = async (): Promise<void> => {
        return this.setState({ showAddUserPrompt: false })
    }

    protected handleUserAdd = async (
        userDto: Partial<UserDto> & { permissions?: AccountUserModel[] },
    ): Promise<void> => {
        let accounts: any[] = []

        if (Array.isArray(userDto.permissions)) {
            const source = userDto.permissions[0]
            const accountRecord: any = {
                accountId: source.getAccountId(),
                roleId: source.getRoleId(),
                flags: source.getFlags(),
                domains: {},
            }

            for (const domain of Array.from(source.getDomainRecords().values())) {
                accountRecord.domains[domain.getDomainId()] = {
                    roleId: domain.getRoleId(),
                    flags: domain.getFlags(),
                }
            }

            accounts.push(accountRecord)
        }

        const { ok, data: user } = await this.userService.addUser(
            { ...userDto, accounts },
            {
                showLoadingScreen: true,
            },
            this.appState,
        )

        if (ok) {
            this.jumpToUser(user)
        }
    }

    protected handleDisable = async (item: any) => {
        const { user } = item.props

        await this.userService.updateUserById(
            user.id,
            {
                status: 'DISABLED',
            },
            {
                showLoadingScreen: true,
            },
        )

        return this.fetchUsers()
    }

    protected handleEnable = async (item: any) => {
        const { user } = item.props

        await this.userService.updateUserById(
            user.id,
            {
                status: 'ACTIVE',
            },
            {
                showLoadingScreen: true,
            },
        )

        return this.fetchUsers()
    }

    protected handleReInvite = async (item: any): Promise<void> => {
        const { user } = item.props

        await this.userService.triggerInviteForUserId(
            user.id,
            this.props.options.account.id,
            {
                showLoadingScreen: true,
            },
            this.appState,
        )

        return this.fetchUsers()
    }

    protected handleResetPassword = async (item: any): Promise<void> => {
        const { user } = item.props
        await this.userService.triggerPasswordResetForUserId(
            user.id,
            this.props.options.account.id,
            {
                showLoadingScreen: true,
            },
            this.appState,
        )
        return this.fetchUsers()
    }

    protected filterUserTypes = async (typeFilter: string[]) => {
        if (!deepEqual(typeFilter, this.state.typeFilter)) {
            typeFilter = typeFilter || []
            await this.setState({ typeFilter })

            return this.fetchUsers()
        }
    }

    protected filterUserStatuses = async (statusFilter: string[]) => {
        if (!deepEqual(statusFilter, this.state.statusFilter)) {
            statusFilter = statusFilter || []
            await this.setState({ statusFilter })

            return this.fetchUsers()
        }
    }

    protected searchUsers = async (value: string): Promise<void> => {
        let query = value.trim()

        if (value.length < 2) {
            query = undefined
        }

        await this.setState({ query })

        return this.fetchUsers()
    }

    protected async fetchUsers(): Promise<void> {
        let update: any = { loading: true }
        await this.setState(update)

        update.loading = false
        update.users = []

        let response: IApiResponse<any[]>
        const options = {
            showLoadingScreen: false,
            query: {
                page: this.state.page,
                limit: this.state.pageSize,
                search: this.state.query,
                type: this.state.typeFilter.join(','),
                status: this.state.statusFilter.join(','),
            },
        }

        if (this.props.options.account?.id) {
            response = await this.userService.fetchAllAccountUsers(this.props.options.account.id, options)
        } else {
            response = await this.userService.fetchAll(options)
        }

        if (response && response.ok) {
            update.users = response.data
            update.paginationLinks = response.meta.links ?? this.state.paginationLinks
            update.usersMeta = response.meta
        }

        return this.setState(update)
    }
}
